import { toNumber } from '../../../utils/number'
import {
  WbsItemAdditionalPropertyEntity,
  WbsItemAdditionalPropertyType,
} from '../../entity/WbsItemAdditionalPropertyEntity'
import { DateTimeV2, dateTimeV2Service } from '../DateTimeV2'
import { DateV2, dateV2Service } from '../DateV2'
import { validateAdditionalPropertyValue } from './validator'

export type ReferenceEntityValueType = {
  uuid: string
  code: string
  name: string
  iconUrl: string
}
export type WbsItemAdditionalPropertyValueType =
  | undefined
  | boolean
  | number
  | string
  | DateV2
  | DateTimeV2
  | string[]
  | ReferenceEntityValueType
export type WbsItemAdditionalPropertyNumberValueType = undefined | number
export type WbsItemAdditionalPropertyTextValueType = undefined | string
export type WbsItemAdditionalPropertyMultiLineTextValueType = undefined | string
export type WbsItemAdditionalPropertyDateValueType = undefined | DateV2
export type WbsItemAdditionalPropertyDateTimeValueType = undefined | DateTimeV2
export type WbsItemAdditionalPropertyCheckboxValueType = undefined | boolean
export type WbsItemAdditionalPropertySelectValueType = undefined | string
export type WbsItemAdditionalPropertyMultiSelectValueType = undefined | string[]
export type WbsItemAdditionalPropertyEntitySearchValueType =
  | undefined
  | ReferenceEntityValueType

export type WbsItemAdditionalPropertyValueObject = {
  wbsItemAdditionalPropertyUuid: string
  propertyType: WbsItemAdditionalPropertyType
  value: WbsItemAdditionalPropertyValueType
}
export type WbsItemAdditionalPropertyValueInput = {
  wbsItemAdditionalPropertyUuid: string
  value: string | undefined
}
export type WbsItemAdditionalPropertyValueDeltaInput = {
  wbsItemAdditionalPropertyUuid: string
  oldValue: string | undefined
  newValue: string | undefined
}
export type WbsItemAdditionalPropertyValueVO = {
  wbsItemAdditionalPropertyUuid: string
  value: WbsItemAdditionalPropertyValueType
}
type WbsItemAdditionalPropertyValues = {
  [wbsItemAdditionalPropertyUuid: string]: WbsItemAdditionalPropertyValueType
}
type WbsItemAdditionalPropertyTypes = {
  [wbsItemAdditionalPropertyUuid: string]: WbsItemAdditionalPropertyType
}
export type WbsItemAdditionalPropertyValuesVO = {
  propertyTypes: WbsItemAdditionalPropertyTypes
  values: WbsItemAdditionalPropertyValues
}

// Constructor
const construct = (): WbsItemAdditionalPropertyValuesVO => {
  return {
    propertyTypes: {},
    values: {},
  }
}
const of = (
  values: WbsItemAdditionalPropertyValueObject[]
): WbsItemAdditionalPropertyValuesVO => {
  const instance: WbsItemAdditionalPropertyValuesVO = {
    propertyTypes: {},
    values: {},
  }
  values.forEach(({ wbsItemAdditionalPropertyUuid, propertyType, value }) => {
    instance.propertyTypes[wbsItemAdditionalPropertyUuid] = propertyType
    instance.values[wbsItemAdditionalPropertyUuid] = deserializeValue(
      propertyType,
      value
    )
  })

  return instance
}
// Services
const hasValue = (value: WbsItemAdditionalPropertyValueType): boolean => {
  return Boolean(value || !isNaN(Number(value)))
}
const getValue = (
  vo: WbsItemAdditionalPropertyValuesVO,
  wbsItemAdditionalPropertyUuid: string
): WbsItemAdditionalPropertyValueType | undefined => {
  return vo.values[wbsItemAdditionalPropertyUuid]
}
const setValue = (
  vo: WbsItemAdditionalPropertyValuesVO,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType
): void => {
  vo.propertyTypes[wbsItemAdditionalProperty.uuid] =
    wbsItemAdditionalProperty.propertyType
  vo.values[wbsItemAdditionalProperty.uuid] = value
}
const equal = (
  vo1: WbsItemAdditionalPropertyValuesVO,
  vo2: WbsItemAdditionalPropertyValuesVO,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity
): boolean => {
  const value1 = getValue(vo1, wbsItemAdditionalProperty.uuid)
  const value2 = getValue(vo2, wbsItemAdditionalProperty.uuid)
  return (
    serializeValue(wbsItemAdditionalProperty.propertyType, value1) ===
    serializeValue(wbsItemAdditionalProperty.propertyType, value2)
  )
}
const existIn = (
  vo: WbsItemAdditionalPropertyValuesVO,
  wbsItemAdditionalPropertyUuid: string
): boolean => {
  return vo.values.hasOwnProperty(wbsItemAdditionalPropertyUuid)
}
const parseValueFromString = (
  propertyType: WbsItemAdditionalPropertyType,
  value: string | null | undefined
): WbsItemAdditionalPropertyValueType => {
  if (value === undefined || value === null) return undefined
  const v = value
  switch (propertyType) {
    case WbsItemAdditionalPropertyType.NUMBER:
      return toNumber(v)
    case WbsItemAdditionalPropertyType.CHECKBOX:
      if (v.toString().match(/^([tT]|check|1)/)) return true
      if (v.toString().match(/^[fF]|0/)) return false
      return undefined
    case WbsItemAdditionalPropertyType.DATE:
      return dateV2Service.fromString(value)
    case WbsItemAdditionalPropertyType.MULTI_SELECT:
      if (Array.isArray(v)) return v
      return v ? v.split(',') : undefined
  }
  return v
}
const serialize = (
  vo: WbsItemAdditionalPropertyValuesVO
): WbsItemAdditionalPropertyValueInput[] => {
  return Object.entries(vo.values).map(
    ([wbsItemAdditionalPropertyUuid, value]) => ({
      wbsItemAdditionalPropertyUuid,
      value: serializeValue(
        vo.propertyTypes[wbsItemAdditionalPropertyUuid],
        value
      ),
    })
  )
}
const serializeDelta = (
  oldVo: WbsItemAdditionalPropertyValuesVO | undefined,
  newVo: WbsItemAdditionalPropertyValuesVO | undefined
): WbsItemAdditionalPropertyValueDeltaInput[] => {
  const wbsItemAdditionalPropertyUuids = new Set<string>()
  const propertyTypes: WbsItemAdditionalPropertyTypes = {}
  const oldValueMap: WbsItemAdditionalPropertyValues = oldVo ? oldVo.values : {}
  const newValueMap: WbsItemAdditionalPropertyValues = newVo ? newVo.values : {}
  if (oldVo) {
    Object.keys(oldValueMap).forEach(v => {
      wbsItemAdditionalPropertyUuids.add(v)
      propertyTypes[v] = oldVo.propertyTypes[v]
    })
  }
  if (newVo) {
    Object.keys(newValueMap).forEach(v => {
      wbsItemAdditionalPropertyUuids.add(v)
      propertyTypes[v] = newVo.propertyTypes[v]
    })
  }

  const deltaInput: WbsItemAdditionalPropertyValueDeltaInput[] = []
  wbsItemAdditionalPropertyUuids.forEach(wbsItemAdditionalPropertyUuid => {
    const propertyType = propertyTypes[wbsItemAdditionalPropertyUuid]
    const oldValue = serializeValue(
      propertyType,
      oldValueMap[wbsItemAdditionalPropertyUuid]
    )
    const newValue = serializeValue(
      propertyType,
      newValueMap[wbsItemAdditionalPropertyUuid]
    )
    if (oldValue === newValue) {
      return
    }

    deltaInput.push({
      wbsItemAdditionalPropertyUuid,
      oldValue,
      newValue,
    })
  })
  return deltaInput
}
const validate = validateAdditionalPropertyValue

export const wbsItemAdditionalPropertyValuesVoService = {
  construct,
  of,
  hasValue,
  getValue,
  setValue,
  equal,
  existIn,
  parseValueFromString,
  serialize,
  serializeDelta,
  validate,
}

// Private
type WbsItemAdditionalPropertyValueTypeForSerialize = undefined | string
const serializeValue = (
  propertyType: WbsItemAdditionalPropertyType,
  value: WbsItemAdditionalPropertyValueType
): WbsItemAdditionalPropertyValueTypeForSerialize => {
  if (value === undefined) {
    return undefined
  }
  switch (propertyType) {
    case WbsItemAdditionalPropertyType.DATE:
      if (value instanceof Date) {
        return dateV2Service.fromVoToApiRequest(value)
      } else if (typeof value === 'string') {
        return value
      } else {
        return String(value)
      }
    case WbsItemAdditionalPropertyType.DATE_TIME:
      if (value instanceof Date) {
        return String(dateTimeV2Service.fromVoToApiRequest(value))
      } else if (typeof value === 'string') {
        return value
      } else {
        return String(value)
      }
    case WbsItemAdditionalPropertyType.ENTITY_SEARCH:
      return (value as ReferenceEntityValueType).uuid
    case WbsItemAdditionalPropertyType.MULTI_SELECT:
      if (!Array.isArray(value) || value.length === 0) return undefined
      return value.join(',')
    case WbsItemAdditionalPropertyType.NUMBER:
    case WbsItemAdditionalPropertyType.TEXT:
    case WbsItemAdditionalPropertyType.MULTI_LINE_TEXT:
    case WbsItemAdditionalPropertyType.CHECKBOX:
    case WbsItemAdditionalPropertyType.SELECT:
    default:
      return String(value)
  }
}
const deserializeValue = (
  propertyType: WbsItemAdditionalPropertyType,
  value: WbsItemAdditionalPropertyValueType
): WbsItemAdditionalPropertyValueType => {
  if (value === undefined) {
    return undefined
  }
  switch (propertyType) {
    case WbsItemAdditionalPropertyType.DATE:
      return dateV2Service.fromApiResponseToVo(value as string | undefined)
    case WbsItemAdditionalPropertyType.DATE_TIME:
      return dateTimeV2Service.fromApiResponseToVo(value as string | undefined)
    case WbsItemAdditionalPropertyType.ENTITY_SEARCH:
      return value as ReferenceEntityValueType
    case WbsItemAdditionalPropertyType.NUMBER:
      return typeof value === 'string' ? toNumber(value) : value
    case WbsItemAdditionalPropertyType.CHECKBOX:
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true'
      }
      return Boolean(value)
    case WbsItemAdditionalPropertyType.MULTI_SELECT:
      if (!value) return undefined
      return String(value).split(',')
    case WbsItemAdditionalPropertyType.TEXT:
    case WbsItemAdditionalPropertyType.MULTI_LINE_TEXT:
    case WbsItemAdditionalPropertyType.SELECT:
    default:
      return String(value)
  }
}
